import React, { useMemo } from 'react';
import { Grid, GridProps, GridSpacing } from '@material-ui/core';
import { useScreenSizeCheck } from '@moderepo/mode-app-components';


/**
 * Extends GridProps but exclude the 'spacing' props. The parent container must specify the spacing for different screen sizes.
 */
export interface ExtGridProps extends Omit<GridProps, 'spacing'> {
    readonly xs_spacing?: GridSpacing;
    readonly sm_spacing?: GridSpacing;
    readonly md_spacing?: GridSpacing;
    readonly lg_spacing?: GridSpacing;
    readonly xl_spacing?: GridSpacing;
}


/**
 * This is an Extension of Material UI's Grid container component. Grid container component DOES NOT support spacing based on screen size.
 * It is a `spacing` prop but the value is fixed. Therefore, we need to extend this component and add support for spacing based on screen
 * size.
 */
export const ExtGrid: React.FC<ExtGridProps & React.PropsWithChildren<React.ReactNode>> = (
    props: ExtGridProps & React.PropsWithChildren<React.ReactNode>,
) => {

    const isSMScreen = useScreenSizeCheck('sm');
    const isMDScreen = useScreenSizeCheck('md');
    const isLGScreen = useScreenSizeCheck('lg');
    const isXLScreen = useScreenSizeCheck('xl');

    // Get the grid's spacing based on the current screen size and the provided spacing settings in props.
    const gridSpacing = useMemo(() => {
        // If screen size is XL, use XL spacing settings if it exists. If not, fall back to LG spacing settings. If LG spacing is not provided
        // either, fall back to MD spacing setting, and so on
        if (isXLScreen && props.xl_spacing) return props.xl_spacing ?? props.lg_spacing ?? props.md_spacing ?? props.sm_spacing ?? props.xs_spacing;

        // Same as XL screen however, we will start with LG spacing setting and then MD and then SM and so on.
        if (isLGScreen && props.lg_spacing) return props.lg_spacing ?? props.md_spacing ?? props.sm_spacing ?? props.xs_spacing;

        if (isMDScreen && props.md_spacing) return props.md_spacing ?? props.sm_spacing ?? props.xs_spacing;
        if (isSMScreen && props.sm_spacing) return props.sm_spacing ?? props.xs_spacing;
        return props.xs_spacing;
    }, [isSMScreen, isMDScreen, isLGScreen, isXLScreen, props.xs_spacing, props.sm_spacing, props.md_spacing, props.lg_spacing, props.xl_spacing]);


    return (
        <Grid {...props} spacing={gridSpacing}>
            {props.children}
        </Grid>
    );
};
